import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AuditTrailPanel = ({ auditLogs, onExportAuditLog }) => {
  const { t } = useTranslation();
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateRange, setDateRange] = useState('last-30-days');

  const actionTypes = [
    { value: '', label: 'All Actions' },
    { value: 'report_generated', label: 'Report Generated' },
    { value: 'report_exported', label: 'Report Exported' },
    { value: 'report_previewed', label: 'Report Previewed' },
    { value: 'report_deleted', label: 'Report Deleted' },
    { value: 'config_changed', label: 'Configuration Changed' },
    { value: 'access_granted', label: 'Access Granted' },
    { value: 'access_denied', label: 'Access Denied' }
  ];

  const userFilters = [
    { value: '', label: 'All Users' },
    { value: 'admin', label: 'Admin Users' },
    { value: 'manager', label: 'Managers' },
    { value: 'auditor', label: 'Auditors' },
    { value: 'system', label: 'System Actions' }
  ];

  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const getActionIcon = (action) => {
    const iconMap = {
      'report_generated': 'FileText',
      'report_exported': 'Download',
      'report_previewed': 'Eye',
      'report_deleted': 'Trash2',
      'config_changed': 'Settings',
      'access_granted': 'CheckCircle',
      'access_denied': 'XCircle'
    };
    return iconMap?.[action] || 'Activity';
  };

  const getActionColor = (action) => {
    const colorMap = {
      'report_generated': 'text-success',
      'report_exported': 'text-primary',
      'report_previewed': 'text-muted-foreground',
      'report_deleted': 'text-destructive',
      'config_changed': 'text-warning',
      'access_granted': 'text-success',
      'access_denied': 'text-destructive'
    };
    return colorMap?.[action] || 'text-muted-foreground';
  };

  const getRiskLevel = (action, details) => {
    if (action === 'access_denied' || action === 'report_deleted') return 'high';
    if (action === 'config_changed' || action === 'access_granted') return 'medium';
    return 'low';
  };

  const filteredLogs = auditLogs?.filter(log => {
    const actionMatch = !filterAction || log?.action === filterAction;
    const userMatch = !filterUser || log?.userRole === filterUser;
    return actionMatch && userMatch;
  });

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Audit Trail</h2>
              <p className="text-sm text-muted-foreground">Compliance activity monitoring and logging</p>
            </div>
          </div>
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={() => onExportAuditLog(filteredLogs)}
          >
            Export Audit Log
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label={t('complianceReportsPage.audit.filterByAction')}
            options={actionTypes}
            value={filterAction}
            onChange={setFilterAction}
            placeholder={t('complianceReportsPage.audit.allActions')}
          />
          <Select
            label={t('complianceReportsPage.audit.filterByUserRole')}
            options={userFilters}
            value={filterUser}
            onChange={setFilterUser}
            placeholder={t('complianceReportsPage.audit.allUsers')}
          />
          <Select
            label={t('complianceReportsPage.audit.dateRange')}
            options={dateRanges}
            value={dateRange}
            onChange={setDateRange}
          />
        </div>
      </div>
      {/* Audit Log Entries */}
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {filteredLogs?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Audit Entries</h3>
            <p className="text-muted-foreground">No audit entries match your current filters.</p>
          </div>
        ) : (
          filteredLogs?.map((log) => (
            <div key={log?.id} className="p-4 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start space-x-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  getRiskLevel(log?.action, log?.details) === 'high' ? 'bg-destructive/10' :
                  getRiskLevel(log?.action, log?.details) === 'medium' ? 'bg-warning/10' : 'bg-success/10'
                }`}>
                  <Icon 
                    name={getActionIcon(log?.action)} 
                    size={16} 
                    className={getActionColor(log?.action)} 
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-foreground">{log?.description}</h4>
                    <div className="flex items-center space-x-2">
                      {getRiskLevel(log?.action, log?.details) === 'high' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                          <Icon name="AlertTriangle" size={10} className="mr-1" />
                          High Risk
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.timestamp)?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="User" size={12} />
                      <span>{log?.userName} ({log?.userRole})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Globe" size={12} />
                      <span>{log?.ipAddress}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Monitor" size={12} />
                      <span>{log?.userAgent}</span>
                    </div>
                  </div>

                  {log?.details && (
                    <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                      <strong>Details:</strong> {log?.details}
                    </div>
                  )}

                  {log?.reportId && (
                    <div className="mt-2 flex items-center space-x-2 text-xs">
                      <Icon name="Link" size={12} className="text-muted-foreground" />
                      <span className="text-muted-foreground">Report ID: {log?.reportId}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Footer Stats */}
      {filteredLogs?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Total Events: {filteredLogs?.length}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={14} className="text-destructive" />
              <span className="text-muted-foreground">
                High Risk: {filteredLogs?.filter(log => getRiskLevel(log?.action, log?.details) === 'high')?.length}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">
                Unique Users: {new Set(filteredLogs.map(log => log.userName))?.size}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">
                Last Activity: {new Date(Math.max(...filteredLogs.map(log => new Date(log.timestamp))))?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTrailPanel;